import type { StatusMap } from "@vixio/ui";
// Tipos das RPCs do core (core.set_setting) vêm do pacote de UI do core.
import type {} from "@vixio/core-ui";

export const CONN_STATUS: StatusMap = {
  pendente: { label: "pendente", tone: "warn" },
  ativa: { label: "ativa", tone: "ok" },
  erro: { label: "com erro", tone: "crit" },
  expirada: { label: "expirada", tone: "warn" },
  revogada: { label: "revogada", tone: "neutral" },
};
export const HEALTH: StatusMap = {
  desconhecida: { label: "sem uso ainda", tone: "neutral" },
  ok: { label: "saudável", tone: "ok" },
  degradada: { label: "degradada", tone: "warn" },
  falhando: { label: "falhando", tone: "crit" },
};
export const DELIVERY_STATUS: StatusMap = {
  aceito: { label: "aceito", tone: "ok" },
  duplicado: { label: "duplicado", tone: "neutral" },
  assinatura_invalida: { label: "assinatura inválida", tone: "crit" },
  json_invalido: { label: "JSON inválido", tone: "crit" },
  desativado: { label: "desativado", tone: "warn" },
  grande_demais: { label: "grande demais", tone: "crit" },
};
export const SCHEME: Record<string, string> = { vixio_v1: "Vixio v1 (t=…,v1=… sobre t.corpo, 5 min)", hex_sha256: "HMAC-SHA256 hex do corpo (sha256=…)" };

export interface Provider {
  key: string;
  label: string;
  auth_kind: "oauth2" | "api_key" | "hmac" | "none";
  default_scopes: string[];
  docs_url: string | null;
}

export interface ConnectionView {
  id: string;
  provider: string;
  provider_label: string;
  label: string;
  account_email: string | null;
  scopes: string[];
  status: string;
  health: string;
  consecutive_failures: number;
  last_check_at: string | null;
  last_ok_at: string | null;
  last_error: string | null;
  token_expires_at: string | null;
  config: Record<string, unknown>;
  created_at: string;
  /** Conexão pessoal (de um usuário); nula = da organização. */
  owner_user?: string | null;
}

export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  rate_limit_per_minute: number;
  status: "ativa" | "revogada";
  expires_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface Endpoint {
  id: string;
  key: string;
  label: string;
  provider: string;
  signature_scheme: string;
  signature_header: string;
  status: "ativo" | "desativado";
  last_received_at: string | null;
  created_at: string;
}

export interface Delivery {
  id: string;
  endpoint_id: string;
  body_sha256: string;
  size_bytes: number;
  status: string;
  event_id: number | null;
  created_at: string;
}

/** Linha de integrations.list_system_secrets (nunca o valor). */
export interface SystemSecret {
  key: string;
  catalog_key: string;
  label: string;
  provider: string;
  description: string;
  per_item: boolean;
  configured: boolean;
  last4: string | null;
  updated_at: string | null;
  updated_by: string | null;
  updated_by_email: string | null;
  sort: number;
}

export type TestProvider = "smtp" | "asaas" | "openrouter" | "google" | "nfse" | "hub" | "gotenberg" | "pdf_ocr" | "google_calendar"
  | "transcricao" | "google_meet";

export interface ConnectionTest {
  id: string;
  org_id: string;
  provider: TestProvider;
  params: Record<string, unknown>;
  status: "pendente" | "executando" | "ok" | "erro" | "nao_suportado";
  message: string | null;
  created_at: string;
  finished_at: string | null;
}

export const TEST_STATUS: StatusMap = {
  pendente: { label: "na fila", tone: "neutral" },
  executando: { label: "testando…", tone: "neutral" },
  ok: { label: "conectado", tone: "ok" },
  erro: { label: "falhou", tone: "crit" },
  nao_suportado: { label: "sem teste", tone: "warn" },
};

export const SECRET_GROUP: Record<string, string> = {
  email: "E-mail", whatsapp: "WhatsApp e atendimento", cobranca: "Cobrança", nfse: "NFS-e", ia: "IA", google: "Google",
  automacoes: "Automações", outros: "Outros",
};

type U = string;
declare module "@vixio/data" {
  interface RpcRegistry {
    "integrations.set_system_secret": { args: { p_org: U; p_key: string; p_value: string }; returns: { key: string; last4: string | null; updated_at: string; ref: string } };
    "integrations.clear_system_secret": { args: { p_org: U; p_key: string }; returns: boolean };
    "integrations.list_system_secrets": { args: { p_org: U }; returns: SystemSecret[] };
    "integrations.request_connection_test": { args: { p_org: U; p_provider: TestProvider; p_params?: Record<string, unknown> }; returns: U };
    "integrations.create_connection": { args: { p_org: U; p_provider: string; p_label: string; p_scopes?: string[] | null; p_config?: Record<string, unknown> }; returns: U };
    "integrations.begin_oauth": { args: { p_connection: U; p_redirect_uri: string }; returns: { state: string; auth_url: string } };
    "integrations.revoke_connection": { args: { p_connection: U }; returns: null };
    "integrations.start_google_oauth": { args: { p_connection: U }; returns: { connection_id: U; redirect_uri: string; auth_url: string } };
    "integrations.connect_my_google": { args: { p_org: U }; returns: { connection_id: U; redirect_uri: string; auth_url: string } };
    "integrations.create_api_key": { args: { p_org: U; p_name: string; p_scopes: string[]; p_rate_limit_per_minute?: number; p_expires_at?: string | null }; returns: { id: U; prefix: string; key: string } };
    "integrations.revoke_api_key": { args: { p_key: U }; returns: null };
    "integrations.create_inbound_endpoint": {
      args: { p_org: U; p_key: string; p_label: string; p_signature_scheme?: string; p_signature_header?: string | null; p_provider?: string };
      returns: { endpoint_id: U; path: string; secret: string };
    };
    "integrations.rotate_endpoint_secret": { args: { p_endpoint: U }; returns: string };
    "integrations.set_endpoint_status": { args: { p_endpoint: U; p_status: "ativo" | "desativado" }; returns: null };
    "core.get_setting": { args: { p_org: U; p_key: string; p_default?: unknown }; returns: unknown };
  }
}

/** Recursos da API externa (escopos recurso:read ou recurso:write). */
export const API_RESOURCES = [
  { key: "people", label: "Pessoas" },
  { key: "sales", label: "Comercial" },
  { key: "forms", label: "Formulários" },
  { key: "finance", label: "Financeiro" },
  { key: "agenda", label: "Agenda" },
];
